import { ElementType, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, chipClasses, ChipProps, emphasize, styled } from '@mui/material';
import classNames from 'classnames';

import { RouterChip, RouterChipProps } from '@components/base';
import { collectionTypeGuard, PageContentType } from '@models/Content';

import { ChipLink, ChipLinkProps } from './ChipLink';
import { ContentIcon } from './ContentIcon';

export type ContentTypeChipProps = Partial<
  Omit<ChipProps & RouterChipProps & ChipLinkProps, 'color' | 'variant'>
> & {
  contentType: PageContentType;
};

type StyledChipProps = ContentTypeChipProps & { component: ElementType };

const StyledChip = styled(({ contentType, component: Component, ...props }: StyledChipProps) => (
  <Component {...props} />
))`
  background: ${({ contentType, theme }) => theme.palette.custom.content[contentType]};
  color: ${({ contentType, theme }) =>
    theme.palette.getContrastText(theme.palette.custom.content[contentType])};

  &.${chipClasses.clickable} {
    &:hover {
      background: ${({ contentType, theme }) =>
        emphasize(theme.palette.custom.content[contentType], theme.palette.action.hoverOpacity)};
      cursor: pointer;
    }

    &:focus-visible {
      background: ${({ contentType, theme }) =>
        emphasize(theme.palette.custom.content[contentType], theme.palette.action.focusOpacity)};
    }
  }

  .click-icon {
    opacity: ${({ theme }) => 1 - theme.palette.action.hoverOpacity};
  }
`;

export const ContentTypeChip: FC<ContentTypeChipProps> = ({
  className,
  contentType,
  href,
  onClick,
  size = 'small',
  to,
  ...rest
}) => {
  const { t } = useTranslation();

  const isCollection = collectionTypeGuard(contentType);
  const baseContentType = isCollection ? 'collection' : contentType;

  const Component = to ? RouterChip : href ? ChipLink : Chip;

  return (
    <StyledChip
      className={classNames(className, { [chipClasses.clickable]: to || href })}
      component={Component}
      contentType={baseContentType}
      href={href}
      icon={
        <ContentIcon color='inherit' contentType={contentType} fontSize='inherit' hideTooltip />
      }
      label={t(`contentType.${baseContentType}`)}
      onClick={onClick}
      size={size}
      tabIndex={0}
      target='_blank'
      title={t(`contentType.${contentType}`)}
      to={to}
      {...rest}
    />
  );
};
